import { Edit, Settings, Trash2, UserCheck2, UserX } from "lucide-react";
import { memo, useState } from "react";

import { UserPageItemDTO } from "@/common/api/sdk";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  ConfirmModal,
} from "@/common/components";
import { getRoleLabel } from "@/common/lib";

interface UserTableProps {
  isLoading: boolean;
  users: UserPageItemDTO[];
  onEditUser: (userId: string) => void;
  onOpenPermissions: (user: UserPageItemDTO) => void;
  onConfirmDelete: (userId: string, userName: string) => void;
  onConfirmBlocking: (userId: string, isBlocking: boolean) => void;
}

export const UserTable = memo(function UserTable({
  isLoading,
  users,
  onEditUser,
  onOpenPermissions,
  onConfirmDelete,
  onConfirmBlocking,
}: UserTableProps) {
  const [blockingModal, setBlockingModal] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
    isBlocking: boolean;
  }>({
    isOpen: false,
    userId: "",
    userName: "",
    isBlocking: false,
  });

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    userId: string;
    userName: string;
  }>({
    isOpen: false,
    userId: "",
    userName: "",
  });

  const handleBlockingClick = (user: UserPageItemDTO, isBlocking: boolean) => {
    setBlockingModal({
      isOpen: true,
      userId: user.id ?? "",
      userName: user.username || "пользователя",
      isBlocking,
    });
  };

  const handleConfirmBlocking = () => {
    onConfirmBlocking(blockingModal.userId, blockingModal.isBlocking);
    setBlockingModal({ isOpen: false, userId: "", userName: "", isBlocking: false });
  };

  const handleDeleteClick = (user: UserPageItemDTO) => {
    setDeleteModal({
      isOpen: true,
      userId: user.id ?? "",
      userName: user.username || "пользователя",
    });
  };

  const handleConfirmDelete = () => {
    onConfirmDelete(deleteModal.userId, deleteModal.userName);
    setDeleteModal({ isOpen: false, userId: "", userName: "" });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Список пользователей</CardTitle>
      </CardHeader>
      <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Загрузка...</p>
          </div>
        ) : !users || users.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Данные не найдены</p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full" style={{ scrollbarWidth: "thin" }}>
            <div className="p-6">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>Компания</TableHead>
                    <TableHead>ИНН</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>Дата создания</TableHead>
                    <TableHead>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex flex-col items-center gap-2">
                          <div className="font-medium">{user.username}</div>
                          {!user.active && (
                            <Badge
                              variant="outline"
                              className="text-xs text-red-600 border-red-600"
                            >
                              Заблокирован
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{user.companyName || "—"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{user.inn || "—"}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant={
                            user.role === "admin" || user.role === "ADMIN" ? "default" : "secondary"
                          }
                        >
                          {getRoleLabel(user.role ?? "")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString("ru-RU")
                          : "Не указано"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onEditUser(user.id ?? "")}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Изменить
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onOpenPermissions(user)}
                            className="text-blue-600 hover:text-white"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Права
                          </Button>

                          {user.active ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBlockingClick(user, false)}
                              className="text-orange-600 hover:text-white"
                            >
                              <UserX className="w-4 h-4 mr-1" />
                              Заблокировать
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBlockingClick(user, true)}
                              className="text-green-600 hover:text-white"
                            >
                              <UserCheck2 className="w-4 h-4 mr-1" />
                              Разблокировать
                            </Button>
                          )}

                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Удалить
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>

      <ConfirmModal
        isOpen={blockingModal.isOpen}
        title={
          blockingModal.isBlocking ? "Разблокировать пользователя" : "Заблокировать пользователя"
        }
        message={`Вы точно хотите ${
          blockingModal.isBlocking ? "разблокировать" : "заблокировать"
        } пользователя - ${blockingModal.userName}?`}
        onConfirm={handleConfirmBlocking}
        onCancel={() =>
          setBlockingModal({ isOpen: false, userId: "", userName: "", isBlocking: false })
        }
        confirmText={blockingModal.isBlocking ? "Разблокировать" : "Заблокировать"}
        cancelText="Отмена"
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title="Удаление пользователя"
        message={`Вы точно хотите удалить пользователя - ${deleteModal.userName}?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModal({ isOpen: false, userId: "", userName: "" })}
        confirmText="Удалить"
        cancelText="Отмена"
      />
    </Card>
  );
});
